import { useGT } from 'gt-react';
import type {
  EffectivePolicy,
  InventoryPolicyOverrideRow,
} from 'src/api/rpc/hooks/procurement/useInventoryPolicyOverrides';
import {
  OVERRIDE_FIELDS,
  fmtFieldValue,
  nextActiveSetter,
  overrideFieldLabel,
  revokeFallbackSource,
  type PendingOverrideValues,
} from './policyOverrideRows';

// The before/after tables inside the confirm dialogs: what creating this
// override changes, and what revoking one reverts. Both are the same table
// (one row per field the override sets, the outgoing value struck through), so
// they share the shell and differ only in the columns they fill.

const OUTGOING = 'py-1 pr-4 text-gray-400 line-through';

interface PreviewCell {
  text: string;
  className: string;
}

interface PreviewRow {
  label: string;
  cells: readonly PreviewCell[];
}

function PreviewTable({
  headers,
  rows,
}: {
  /** The label column first, then one per cell each row supplies. */
  headers: readonly string[];
  rows: readonly PreviewRow[];
}) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-gray-500">
          {headers.map((header) => (
            <th key={header} className="py-1 pr-4 font-medium">
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.label} className="border-t border-gray-100">
            <td className="py-1 pr-4 font-medium text-gray-700">{r.label}</td>
            {r.cells.map((cell, i) => (
              <td key={headers[i + 1]} className={cell.className}>
                {cell.text}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// What the new override will change: per field it sets, the current effective
// value → the new value. A new override is the most-recent, so it wins every
// field it sets immediately.
export function CreatePreviewTable({
  values,
  effective,
}: {
  values: PendingOverrideValues;
  effective: EffectivePolicy | null;
}) {
  const gt = useGT();
  const rows = OVERRIDE_FIELDS.filter((m) => values[m.key] !== undefined).map(
    (m) => ({
      label: overrideFieldLabel(m.key, gt),
      cells: [
        {
          text: fmtFieldValue(m.kind, effective ? effective[m.key] : null, gt),
          className: OUTGOING,
        },
        {
          text: fmtFieldValue(m.kind, values[m.key] ?? null, gt),
          className: 'py-1 font-medium text-blue-700',
        },
      ],
    }),
  );
  return (
    <PreviewTable
      headers={[
        gt('Field', { context: 'override preview column' }),
        gt('Current', { context: 'override preview column' }),
        gt('New', { context: 'override preview column' }),
      ]}
      rows={rows}
    />
  );
}

// What each field this override sets will revert to once it's revoked: the next
// active override (excluding this one) that sets the field, else the base value.
export function RevokePreviewTable({
  override: o,
  overrides,
  effective,
}: {
  override: InventoryPolicyOverrideRow;
  overrides: readonly InventoryPolicyOverrideRow[];
  effective: EffectivePolicy | null;
}) {
  const gt = useGT();
  const rows = OVERRIDE_FIELDS.filter((m) => o[m.key] != null).map((m) => {
    const fallback = nextActiveSetter(overrides, o.id, m.key);
    const hasBaseValue = 'baseKey' in m;
    const revertsTo = fallback
      ? fallback[m.key]
      : effective && hasBaseValue
        ? effective[m.baseKey]
        : null;
    return {
      label: overrideFieldLabel(m.key, gt),
      cells: [
        {
          text: fmtFieldValue(m.kind, o[m.key], gt),
          className: OUTGOING,
        },
        {
          text: fmtFieldValue(m.kind, revertsTo, gt),
          className: 'py-1 pr-4 font-medium text-gray-900',
        },
        {
          text: revokeFallbackSource(fallback, gt, hasBaseValue, m.key),
          className: 'py-1 text-xs text-gray-500',
        },
      ],
    };
  });
  return (
    <PreviewTable
      headers={[
        gt('Field', { context: 'revoke preview column' }),
        gt('Removing', { context: 'revoke preview column' }),
        gt('Reverts to', { context: 'revoke preview column' }),
        gt('From', { context: 'revoke preview column' }),
      ]}
      rows={rows}
    />
  );
}
